import React, { useCallback } from 'react';
import { useStateReducer, showNotification, captureException } from 'r/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { AnalysisData } from '../types';

interface DeleteModalProps {
  isOpen: boolean;
  selectedAnalyses: AnalysisData[];
  onClose: () => void;
  onConfirm: (analysisIds: string[]) => Promise<void>;
}

export default function ModalDeleteAnalysis({
  isOpen,
  selectedAnalyses,
  onClose,
  onConfirm,
}: DeleteModalProps) {
  const [{ isSubmitting }, ss] = useStateReducer({
    isSubmitting: false,
  });

  const handleSubmit = useCallback(async () => {
    if (selectedAnalyses.length === 0) return;
    ss({ isSubmitting: true });
    try {
      await onConfirm(selectedAnalyses.map((a) => a.id));
    } catch (error) {
      captureException(error);
      showNotification('error', 'Failed to delete analyses');
    } finally {
      ss({ isSubmitting: false });
    }
  }, [selectedAnalyses, onConfirm, ss]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Delete Analyses</DialogTitle>
        </DialogHeader>

        <div className="max-h-[40vh] overflow-y-auto space-y-2">
          {selectedAnalyses.map((analysis) => (
            <div key={analysis.id} className="p-3 border rounded-lg">
              <div className="font-semibold">{analysis.tweetData.name}</div>
              <div className="text-sm text-secondary truncate">
                {analysis.tweetData.text}
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Deleting...' : 'Confirm Delete'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
